import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Confirm from 'shared/components/confirm/confirm';
import Spinner from 'shared/components/spinner/spinner';
import { PermissionList } from 'shared/interface/enum';
import { checkPermission } from 'shared/util/permission';
import { AI_CONVERSATIONS_PER_PAGE, AI_SEARCH_DEBOUNCE_MS } from 'shared/constants/constants';
import { useDebounce } from 'shared/hooks/useDebounce';
import {
	AiConversation,
	formatConversationTime,
	getConversationPreview,
	getConversationTitle,
	parseListItems,
	parsePagination
} from '../interface/ai';
import { deleteConversation, fetchConversations, updateConversationTitle } from '../util/aiActions';

interface Props {
	conversations: AiConversation[];
	setConversations: React.Dispatch<React.SetStateAction<AiConversation[]>>;
	selectedConversationId: string | null;
	onSelectConversation: (conversation: AiConversation | null) => void;
	onConversationDeleted: (conversationId: string) => void;
	onConversationUpdated: (conversation: AiConversation) => void;
	refreshToken?: number;
}

const LIST_SCROLL_THRESHOLD = 80;

const ConversationListPanel: React.FC<Props> = ({
	conversations,
	setConversations,
	selectedConversationId,
	onSelectConversation,
	onConversationDeleted,
	onConversationUpdated,
	refreshToken = 0
}) => {
	const canList = checkPermission({
		anyOnePermission: [PermissionList.AiConversationList, PermissionList.FullAccess]
	});
	const canAsk = checkPermission({
		anyOnePermission: [PermissionList.AiConversationAsk, PermissionList.FullAccess]
	});
	const canUpdateTitle = checkPermission({
		anyOnePermission: [PermissionList.AiConversationUpdateTitle, PermissionList.FullAccess]
	});
	const canDelete = checkPermission({
		anyOnePermission: [PermissionList.AiConversationDelete, PermissionList.FullAccess]
	});

	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [error, setError] = useState('');
	const [searchInput, setSearchInput] = useState('');
	const [editingConversation, setEditingConversation] = useState<AiConversation | null>(null);
	const [editTitle, setEditTitle] = useState('');
	const [savingTitle, setSavingTitle] = useState(false);
	const [deletingConversation, setDeletingConversation] = useState<AiConversation | null>(null);
	const [deletingId, setDeletingId] = useState<string | null>(null);

	const debouncedSearch = useDebounce(searchInput, AI_SEARCH_DEBOUNCE_MS);
	const loadRequestId = useRef(0);
	const loadingMoreRef = useRef(false);
	const currentPageRef = useRef(1);
	const listRef = useRef<HTMLDivElement>(null);
	const activeSearchRef = useRef(debouncedSearch.trim());
	activeSearchRef.current = debouncedSearch.trim();

	const clearLoadingMore = useCallback(() => {
		loadingMoreRef.current = false;
		setLoadingMore(false);
	}, []);

	const loadConversations = useCallback(
		(search: string, mode: 'initial' | 'more' = 'initial', page = 1) => {
			if (!canList) return;

			const requestSearch = search.trim();
			const requestId = ++loadRequestId.current;

			if (mode === 'more' && loadingMoreRef.current) return;

			if (mode === 'initial') {
				currentPageRef.current = 1;
				loadingMoreRef.current = false;
				setLoadingMore(false);
				setConversations([]);
				setLoading(true);
				setError('');
				setHasMore(true);
			} else {
				loadingMoreRef.current = true;
				setLoadingMore(true);
			}

			const params = {
				page: mode === 'more' ? page : 1,
				perPage: AI_CONVERSATIONS_PER_PAGE,
				orderBy: 'lastMessageAt',
				sort: 'desc' as const,
				...(requestSearch ? { search: requestSearch } : {})
			};

			fetchConversations(params)
				.then((res) => {
					if (requestId !== loadRequestId.current) {
						if (mode === 'more') clearLoadingMore();
						return;
					}
					if (requestSearch !== activeSearchRef.current) {
						if (mode === 'more') clearLoadingMore();
						return;
					}

					const items = parseListItems<AiConversation>(res);
					const { total } = parsePagination(res);
					const requestPage = mode === 'more' ? page : 1;
					const hasMorePages =
						items.length === AI_CONVERSATIONS_PER_PAGE &&
						(total > 0
							? requestPage * AI_CONVERSATIONS_PER_PAGE < total
							: items.length === AI_CONVERSATIONS_PER_PAGE);

					if (mode === 'more') {
						setConversations((prev) => {
							const existingIds = new Set(prev.map((item) => item.conversationId));
							const appended = items.filter((item) => !existingIds.has(item.conversationId));
							return [...prev, ...appended];
						});
						currentPageRef.current = requestPage;
						setHasMore(hasMorePages);
						clearLoadingMore();
						return;
					}

					setConversations(items);
					currentPageRef.current = 1;
					setHasMore(hasMorePages);
					setLoading(false);
				})
				.catch(() => {
					if (requestId !== loadRequestId.current) return;
					if (requestSearch !== activeSearchRef.current) return;

					if (mode === 'more') {
						clearLoadingMore();
						return;
					}

					setConversations([]);
					setLoading(false);
					setHasMore(false);
					setError('Unable to load conversations. Please try again.');
				});
		},
		[canList, clearLoadingMore, setConversations]
	);

	useEffect(() => {
		loadConversations(debouncedSearch, 'initial');
	}, [debouncedSearch, loadConversations, refreshToken]);

	const handleListScroll = useCallback(() => {
		const container = listRef.current;
		if (!container || loading || loadingMore || !hasMore) return;

		const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
		if (distanceFromBottom > LIST_SCROLL_THRESHOLD) return;

		loadConversations(debouncedSearch, 'more', currentPageRef.current + 1);
	}, [debouncedSearch, hasMore, loadConversations, loading, loadingMore]);

	const handleStartEditTitle = (event: React.MouseEvent, conversation: AiConversation) => {
		event.stopPropagation();
		setEditingConversation(conversation);
		setEditTitle(getConversationTitle(conversation));
	};

	const handleSaveTitle = async () => {
		if (!editingConversation || !editTitle.trim()) return;

		setSavingTitle(true);
		try {
			const updated = await updateConversationTitle(editingConversation.conversationId, editTitle.trim());
			onConversationUpdated(updated);
			setConversations((prev) =>
				prev.map((item) => (item.conversationId === updated.conversationId ? { ...item, ...updated } : item))
			);
			toast.success('Conversation title updated.');
			setEditingConversation(null);
		} catch {
			toast.error('Failed to update conversation title.');
		} finally {
			setSavingTitle(false);
		}
	};

	const handleDeleteClick = (event: React.MouseEvent, conversation: AiConversation) => {
		event.stopPropagation();
		setDeletingConversation(conversation);
	};

	const handleConfirmDelete = async () => {
		if (!deletingConversation) return;

		setDeletingId(deletingConversation.conversationId);
		try {
			await deleteConversation(deletingConversation.conversationId);
			setConversations((prev) =>
				prev.filter((item) => item.conversationId !== deletingConversation.conversationId)
			);
			onConversationDeleted(deletingConversation.conversationId);
			toast.success('Conversation deleted.');
			setDeletingConversation(null);
		} catch {
			toast.error('Failed to delete conversation.');
		} finally {
			setDeletingId(null);
		}
	};

	if (!canList && !canAsk) {
		return (
			<div className="telegram-conversations__groups">
				<div className="telegram-conversations__empty">
					<i className="fa fa-lock" />
					<span>You do not have permission to list conversations.</span>
				</div>
			</div>
		);
	}

	if (!canList && canAsk) {
		return (
			<div className="telegram-conversations__groups">
				<div className="telegram-conversations__panel-header telegram-conversations__panel-header--accent">
					<div className="telegram-conversations__panel-title">
						<i className="fa fa-magic" />
						<h5>AI Assistant</h5>
					</div>
				</div>
				<div className="ai-assistant__sidebar-actions">
					<button type="button" className="btn btn-light btn-sm" onClick={() => onSelectConversation(null)}>
						<i className="fa fa-plus" /> New chat
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="telegram-conversations__groups">
			<div className="telegram-conversations__panel-header telegram-conversations__panel-header--accent">
				<div className="telegram-conversations__panel-title">
					<i className="fa fa-magic" />
					<h5>AI Assistant</h5>
				</div>
				<span className="telegram-conversations__group-count">{conversations.length} chats</span>
			</div>
			<div className="ai-assistant__sidebar-actions">
				<button type="button" className="btn btn-light btn-sm" onClick={() => onSelectConversation(null)}>
					<i className="fa fa-plus" /> New chat
				</button>
			</div>
			<div className="telegram-conversations__search">
				<i className="fa fa-search telegram-conversations__search-icon" />
				<input
					type="text"
					className="form-control telegram-conversations__search-input"
					placeholder="Search conversations..."
					value={searchInput}
					onChange={(event) => setSearchInput(event.target.value)}
				/>
			</div>
			<div className="telegram-conversations__group-list" ref={listRef} onScroll={handleListScroll}>
				{loading && conversations.length === 0 ? (
					<div className="telegram-conversations__loading">
						<Spinner />
					</div>
				) : null}
				{error ? (
					<div className="telegram-conversations__empty telegram-conversations__empty--error">{error}</div>
				) : null}
				{!loading && !error && conversations.length === 0 ? (
					<div className="telegram-conversations__empty">
						<i className="fa fa-inbox" />
						<span>No conversations yet</span>
					</div>
				) : null}
				{conversations.map((conversation) => {
					const isSelected = conversation.conversationId === selectedConversationId;
					const isDeleting = deletingId === conversation.conversationId;

					return (
						<div
							key={conversation.conversationId}
							className={`telegram-conversations__group-item ${isSelected ? 'is-active' : ''}`}
						>
							<button
								type="button"
								className="telegram-conversations__group-item-main"
								onClick={() => onSelectConversation(conversation)}
							>
								<span className="telegram-conversations__group-avatar">
									{getConversationTitle(conversation).slice(0, 1).toUpperCase()}
								</span>
								<span className="telegram-conversations__group-meta">
									<span className="telegram-conversations__group-row-top">
										<span className="telegram-conversations__group-name">
											{getConversationTitle(conversation)}
										</span>
										{conversation.lastMessageAt ? (
											<span className="telegram-conversations__group-updated">
												{formatConversationTime(conversation.lastMessageAt)}
											</span>
										) : null}
									</span>
									<span className="telegram-conversations__group-subtitle">
										{getConversationPreview(conversation)}
									</span>
								</span>
							</button>
							<div className="telegram-conversations__group-actions">
								{canUpdateTitle ? (
									<button
										type="button"
										className="telegram-conversations__group-action-btn telegram-conversations__group-action-btn--edit"
										title="Rename conversation"
										onClick={(event) => handleStartEditTitle(event, conversation)}
									>
										<i className="fa fa-pencil" />
									</button>
								) : null}
								{canDelete ? (
									<button
										type="button"
										className="telegram-conversations__group-action-btn"
										title="Delete conversation"
										disabled={isDeleting}
										onClick={(event) => handleDeleteClick(event, conversation)}
									>
										<i className={`fa ${isDeleting ? 'fa-spinner fa-spin' : 'fa-trash'}`} />
									</button>
								) : null}
							</div>
						</div>
					);
				})}
				{loadingMore ? (
					<div className="telegram-conversations__load-more">
						<Spinner />
					</div>
				) : null}
			</div>

			{deletingConversation ? (
				<Confirm
					show
					handleClose={() => setDeletingConversation(null)}
					handleConfirm={handleConfirmDelete}
					loading={!!deletingId}
					message={`Are you sure you want to delete "${getConversationTitle(deletingConversation)}"?`}
					submitBtnText="Delete"
					cancelBtnText="Cancel"
				/>
			) : null}

			{editingConversation ? (
				<div className="ai-assistant__modal-backdrop">
					<div className="ai-assistant__modal">
						<h5>Rename conversation</h5>
						<input
							type="text"
							className="form-control"
							value={editTitle}
							onChange={(event) => setEditTitle(event.target.value)}
							disabled={savingTitle}
						/>
						<div className="ai-assistant__modal-actions">
							<button
								type="button"
								className="btn btn-default"
								onClick={() => setEditingConversation(null)}
								disabled={savingTitle}
							>
								Cancel
							</button>
							<button
								type="button"
								className="btn btn-primary"
								onClick={handleSaveTitle}
								disabled={savingTitle || !editTitle.trim()}
							>
								{savingTitle ? 'Saving...' : 'Save'}
							</button>
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default ConversationListPanel;
